/* eslint-disable max-classes-per-file */
import { Injectable, OnDestroy, inject } from '@angular/core';
import { FAQItem, IFaq, Language, LanguageMap } from '../../models';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { faq as faqFR } from '../../../assets/ressources/faq-fr';
import { faq as faqEN } from '../../../assets/ressources/faq-en';
import { faq as faqES } from '../../../assets/ressources/faq-es';

export class FaqItemFlatNode {
  item: string;
  level: number;
  expandable: boolean;
  hasChild: boolean;
  faqFragment: string;
  isCustom: boolean;
  id: number;
}

export class FaqItemNode {
  children: FaqItemNode[];
  item: string | LanguageMap;
  faqFragment: string;
  parent?: string;
  isCustom?: boolean;
  id: number;
}

@Injectable({
  providedIn: 'root',
})
export class TreeService implements OnDestroy {
  private translocoService = inject(TranslocoService);

  private langSub: Subscription;

  public faq: IFaq;

  public faqFR = faqFR;
  public faqEN = faqEN;
  public faqES = faqES;

  dataChange = new BehaviorSubject<FaqItemNode[]>([]);

  get data(): FaqItemNode[] {
    return this.dataChange.value;
  }

  constructor() {
    this.langSub = this.translocoService.langChanges$.subscribe((lang: Language) => {
      if (lang.includes('en-US')) {
        this.faq = faqEN;
      } else if (lang.includes('es-ES')) {
        this.faq = faqES;
      } else {
        this.faq = faqFR;
      }
      this.initialize();
    });

    this.initialize();
  }

  public initialize() {
    const faqTree = this.buildTree(this.faq.root);
    this.dataChange.next(faqTree);
  }

  private buildTree(node: FAQItem) {
    return node.children.map((child) => {
      if (this.faq[child]?.children.length === 0) {
        return {
          item: this.faq[child].question,
          faqFragment: this.faq[child].fragment,
          children: null,
          id: this.faq[child]?.id,
        };
      }
      return {
        item: this.faq[child].name,
        faqFragment: this.faq[child].fragment,
        children: this.buildTree(this.faq[child]),
        id: this.faq[child]?.id,
      };
    });
  }

  public insertItem(parent: FaqItemNode, name: string | LanguageMap, id: number, isCustom = false, manually = false) {
    if (!parent) {
      return;
    }
    if (!parent.children) {
      parent.children = [];
    }
    parent.children.push({
      item: name,
      parent: parent.id.toString(),
      children: null,
      faqFragment: name?.['fr-FR'] ?? name,
      isCustom: isCustom || manually,
      id,
    });
    this.dataChange.next(this.data);
  }

  public updateItem(node: FaqItemNode, name: string) {
    node.item = {'fr-FR': name};
    this.dataChange.next(this.data);
  }

  public deleteItem(parent: FaqItemNode, nodeId: number): void {
    if (parent.children) {
      parent.children = parent.children.filter((c) => c.id !== nodeId);
      this.dataChange.next(this.data);
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }
}
